import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, IndianRupee, PieChart,
  CreditCard, MessageSquare, Target, Coins, ShoppingBag, Users, Heart, Globe,
  Map, User, LifeBuoy, ChevronLeft, ChevronRight, Landmark,
  Lightbulb, ChevronDown, Compass, Building2, History,
} from 'lucide-react';
import { useState, useEffect } from 'react';

export const NAV_GROUPS = [
  {
    label: 'OVERVIEW',
    items: [
      { label: 'Dashboard',    path: '/dashboard',         icon: LayoutDashboard },
    ],
  },
  {
    label: 'MONEY',
    items: [
      { label: 'Income',       path: '/income',            icon: IndianRupee },
      { label: 'Budget',       path: '/budget',            icon: PieChart },
    ],
  },
  {
    label: 'LOANS',
    items: [
      { label: 'EMI Manager',  path: '/emis',              icon: Landmark },
      { label: 'Hand Loans',   path: '/hand-loans',        icon: Heart },
      { label: 'Credit Cards', path: '/credit-cards',      icon: CreditCard },
      // { label: 'Chit Funds',   path: '/chit-funds',        icon: Users },
    ],
  },
  {
    label: 'GOALS',
    items: [
      { label: 'Savings Goals',path: '/savings-goals',     icon: Target },
      { label: 'Investments',  path: '/investments',       icon: Coins },
    ],
  },
  {
    label: 'INSIGHTS',
    collapsible: true,
    items: [
      { label: 'Insights Hub',     path: '/insights',          icon: Lightbulb },
      // { label: 'Journeys',         path: '/journeys',          icon: Compass },
      { label: 'Chanakya',         path: '/chatbot',           icon: MessageSquare },
      { label: 'Smart Buy',        path: '/smart-buy',         icon: ShoppingBag },
      { label: 'Invest Advisor',   path: '/investment-advisor',icon: TrendingUp },
      { label: 'Lifetime Earnings',path: '/lifetime-earnings', icon: TrendingUp },
    ],
  },
  // { label: 'LIFE EVENTS', items: [{ label: 'Build Tracker', path: '/build-tracker', icon: Building2 }] },
  {
    label: 'SOCIAL',
    items: [
      { label: 'Trips',        path: '/trips',             icon: Map },
      { label: 'Group Spend',  path: '/group-expenses',    icon: Users },
      { label: 'Family Circle',path: '/circle',            icon: Globe },
    ],
  },
  {
    label: 'DATA',
    items: [
      { label: 'Historical Entry', path: '/historical-entry', icon: History },
    ],
  },
  {
    label: 'SETTINGS',
    items: [
      { label: 'Profile',      path: '/profile',           icon: User },
      { label: 'Support',      path: '/support',           icon: LifeBuoy },
    ],
  },
];

function NavGroup({ group, collapsed }) {
  const location = useLocation();
  const [open, setOpen] = useState(true);

  const isActive = (path) => location.pathname === path.split('?')[0];
  const hasActive = group.items.some(i => isActive(i.path));

  // Auto-open when navigating to a child route
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);

  return (
    <div className="mb-1">
      {!collapsed && (
        group.collapsible ? (
          <button
            onClick={() => setOpen(o => !o)}
            className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold tracking-widest uppercase
              text-stone-500 dark:text-stone-400 hover:text-orange-500 dark:hover:text-orange-400 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <Lightbulb size={10} />
              {group.label}
            </span>
            <ChevronDown
              size={11}
              className={`transition-transform duration-200 ${open ? 'rotate-0' : '-rotate-90'}`}
            />
          </button>
        ) : (
          <p className="px-4 pt-3 pb-1 text-[10px] font-bold tracking-widest text-stone-400 dark:text-stone-600 uppercase">
            {group.label}
          </p>
        )
      )}

      {/* Items — hidden when collapsed group is closed */}
      <div className={`overflow-hidden transition-all duration-200 ${
        group.collapsible && !collapsed && !open ? 'max-h-0' : 'max-h-96'
      }`}>
        {group.items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={`${item.path}-${item.label}`}
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={`
                flex items-center gap-3 px-4 py-2 mx-2 rounded-lg text-sm font-medium
                transition-colors duration-100 relative
                ${active
                  ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400'
                  : 'text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-900 dark:hover:text-stone-100'
                }
              `}
            >
              {active && (
                <span className="absolute left-0 top-1 bottom-1 w-0.5 bg-orange-500 rounded-full" />
              )}
              <Icon size={16} className="shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle }) {
  return (
    <aside
      className={`
        fixed top-0 left-0 h-full z-30 hidden md:flex flex-col
        bg-white dark:bg-stone-900
        border-r border-stone-200 dark:border-stone-800
        transition-all duration-200
        ${collapsed ? 'w-16' : 'w-60'}
      `}
    >
      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b border-stone-200 dark:border-stone-800 shrink-0">
        {collapsed ? (
          <span className="text-orange-500 font-extrabold text-xl mx-auto">B</span>
        ) : (
          <span className="text-orange-500 font-extrabold text-lg tracking-tight">BudgetMantra</span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-hide">
        {NAV_GROUPS.map((group) => (
          <NavGroup key={group.label} group={group} collapsed={collapsed} />
        ))}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={onToggle}
        className="h-12 flex items-center justify-center border-t border-stone-200 dark:border-stone-800
          text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-800
          transition-colors"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </aside>
  );
}
